import type { ComponentProps, FC, ReactNode } from 'react';

import { config, ContentContainer, HeroContainer, ImageContainer, RightPlaceholderBox } from '@eneco/custom-components/Hero';
import { Relative } from '@eneco/custom-components/Section';
import { Bleed, Box, Grid, Image, PageGrid, Stack } from '@eneco/sparky';
import type { BoxProps } from '@eneco/sparky/types';

export interface CmsHeroShellImage {
  src: string;
  alt?: string;
  sources?: ComponentProps<typeof Image>['sources'];
}

export interface CmsHeroShellProps {
  image?: CmsHeroShellImage;
  backgroundColor?: BoxProps['backgroundColor'];
  backgroundColorBottom?: BoxProps['backgroundColor'];
  isVariableHeight?: boolean;
  isVariableWidth?: boolean;
  isMinMargin?: boolean;
  /** Overlay on the image (Sitecore `jss-promo-box`). */
  promo?: ReactNode;
  /** Left column — where a Hero's headline/CTA children live. */
  left?: ReactNode;
  right?: ReactNode;
  bottom?: ReactNode;
}

/**
 * The Hero's **presentation only** — grid, image framing, the `HeroContainer`/`ContentContainer` CSS
 * recipes and the bleed behaviour of the bottom slot. Knows nothing about any CMS: slots arrive as
 * `ReactNode`, appearance as plain props.
 *
 * It lives here rather than in a container because `@eneco/custom-components` is off-limits to
 * `container:next` (`@nx/enforce-module-boundaries`), and because every container's Hero should share
 * one shell rather than re-deriving the layout. Extracted verbatim from
 * `libs/sitecore/components/content/src/Hero/HeroInner.tsx` so the CMS-agnostic lane and the legacy
 * Sitecore lane render pixel-identically — which is what makes a `?_cms=` A/B comparison meaningful.
 */
export const CmsHeroShell: FC<CmsHeroShellProps> = ({
  image,
  backgroundColor,
  backgroundColorBottom,
  isVariableHeight = false,
  isVariableWidth = false,
  isMinMargin = false,
  promo,
  left,
  right,
  bottom,
}) => (
  <Box backgroundColor={backgroundColorBottom}>
    <HeroContainer backgroundColor={backgroundColor} isVariableWidth={isVariableWidth} isMinMargin={isMinMargin}>
      {image?.src ? (
        <ImageContainer>
          <Grid gridTemplateColumns="40px auto 1fr" gridTemplateRows={{ initial: '20px auto 1fr 40px', lg: '20px auto 1fr' }}>
            <Grid.Item gridColumn="1 / -1" gridRow="1 / -1">
              <Image
                alt={image.alt ?? ''}
                height={{ initial: config.minHeight.initial, sm: config.minHeight.sm, md: config.minHeight.md, lg: '100%' }}
                hasLazyLoad={false}
                fetchPriority="high"
                objectFit="cover"
                sources={image.sources}
                src={image.src}
                width="100%"
              />
            </Grid.Item>
            {promo ? (
              <Grid.Item gridColumn="1 / -1" gridRow={{ initial: '1 / 4', lg: '1 / -1' }}>
                {promo}
              </Grid.Item>
            ) : null}
          </Grid>
        </ImageContainer>
      ) : null}

      <ContentContainer
        alignY="justify"
        hasBottomPlaceholder={Boolean(bottom)}
        hasRightPlaceholder={Boolean(right)}
        isVariableHeight={isVariableHeight}>
        <Stack.Item>
          <PageGrid>
            <PageGrid.Item gridColumn="1/-1">
              <Box
                paddingTop={{ initial: right || isVariableHeight ? '8' : '0', lg: '16' }}
                paddingBottom={{ initial: isVariableHeight ? '24' : '8', lg: bottom ? '8' : '16' }}>
                <Grid gridTemplateColumns={{ initial: '1fr', lg: '1fr 1fr' }} columnGap={{ lg: '8', xl: '16' }}>
                  <Grid.Item order={{ initial: 2, lg: 1 }}>
                    {left ? (
                      <Box paddingTop={{ initial: '2', lg: '0' }} paddingBottom={{ initial: '2', lg: '0' }}>
                        {left}
                      </Box>
                    ) : null}
                  </Grid.Item>
                  <Grid.Item order={{ initial: 1, lg: 2 }}>
                    {right ? (
                      <RightPlaceholderBox paddingTop={{ initial: '2', lg: '0' }} paddingBottom={{ initial: '2', xl: '0' }}>
                        {right}
                      </RightPlaceholderBox>
                    ) : null}
                  </Grid.Item>
                </Grid>
              </Box>
            </PageGrid.Item>
          </PageGrid>
        </Stack.Item>
      </ContentContainer>
    </HeroContainer>

    {bottom ? (
      <Relative>
        <Bleed top={{ initial: isVariableHeight ? '24' : '0', lg: isVariableHeight ? undefined : '24' }}>
          <PageGrid>
            <PageGrid.Item gridColumn="1/-1">{bottom}</PageGrid.Item>
          </PageGrid>
        </Bleed>
      </Relative>
    ) : null}
  </Box>
);

export default CmsHeroShell;
